/**
 * AffiliateApplyPage — a church or ministry asks to come under the
 * Archbishop's covering. Goes straight into the super admin review queue.
 */
import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, CheckCircle2, Loader2, Paperclip, X, ShieldCheck,
} from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import FwmShell from '@/components/fwm/FwmShell';
import { COUNTRIES } from '@/lib/countries';
import { formatCurrency } from '@/lib/currency';

const BLANK = {
  org_name: '', org_type: 'church', pastor_name: '', pastor_title: 'Pastor',
  country: '', city: '', address: '', email: '', phone: '', whatsapp: '',
  website: '', description: '', years_in_ministry: '', congregation_size: '',
  show_contact: true,
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--fwm-muted)]">
      {label}
    </span>
    <div className="mt-2">{children}</div>
    {hint && <span className="mt-1.5 block text-xs text-[color:var(--fwm-muted)]">{hint}</span>}
  </label>
);

export default function AffiliateApplyPage() {
  const [form, setForm] = useState(BLANK);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const { data: settings } = useQuery({
    queryKey: ['affiliate-public-settings'],
    queryFn: () => api.get('/affiliates/settings').then((r) => r.data),
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/affiliates/apply/document', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocs((d) => [...d, data]);
      toast.success(`${data.name} attached`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'That file could not be attached'));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/affiliates/apply', {
        ...form,
        years_in_ministry: form.years_in_ministry === '' ? null : Number(form.years_in_ministry),
        congregation_size: form.congregation_size === '' ? null : Number(form.congregation_size),
        documents: docs,
      });
      setDone(data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'We could not submit that application'));
    } finally {
      setBusy(false);
    }
  };

  const closed = settings && settings.applications_open === false;

  return (
    <FwmShell ctaLabel="Affiliate directory" ctaLink="/affiliated-ministries"
              testId="affiliate-apply-page">
      <section className="mx-auto max-w-[820px] px-6 lg:px-10 pt-16 pb-24">
        <span className="fwm-kicker">Affiliated churches &amp; ministries</span>
        <h1 className="fwm-display text-4xl sm:text-5xl mt-3 leading-[1.05]">
          Apply for affiliation
        </h1>
        <p className="mt-5 text-base leading-relaxed text-[color:var(--fwm-muted)]">
          {settings?.intro || 'Independent churches and ministries under the covering of the '
            + 'Archbishop. Affiliation is not membership of the FWM church network.'}
        </p>

        {settings && (
          <div className="fwm-card mt-8 p-6 flex items-start gap-4" data-testid="affiliate-fee-note">
            <ShieldCheck className="h-5 w-5 shrink-0 text-[color:var(--fwm-accent-dark)] mt-0.5" />
            <p className="text-sm leading-relaxed text-[color:var(--fwm-muted)]">
              Applications are reviewed by the Archbishop's office. On approval you are invoiced an
              annual membership fee of{' '}
              <strong className="text-[color:var(--fwm-ink)]">
                {formatCurrency(settings.fee_amount, settings.currency)}
              </strong>{' '}
              and affiliation becomes active once payment is confirmed.
            </p>
          </div>
        )}

        {done ? (
          <div className="fwm-card mt-10 p-10 text-center" data-testid="affiliate-apply-success">
            <CheckCircle2 className="h-9 w-9 mx-auto text-emerald-600" />
            <h2 className="fwm-display text-2xl mt-5">Application received</h2>
            <p className="mt-3 text-sm leading-relaxed text-[color:var(--fwm-muted)]">{done.message}</p>
            <Link to="/affiliated-ministries" className="fwm-btn mt-8 inline-flex"
                  data-testid="affiliate-apply-done-link">
              See the affiliate directory <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : closed ? (
          <div className="fwm-card mt-10 p-10 text-center" data-testid="affiliate-apply-closed">
            <h2 className="fwm-display text-2xl">Applications are closed for now</h2>
            <p className="mt-3 text-sm text-[color:var(--fwm-muted)]">
              Please check back soon — the office reopens affiliation intakes periodically.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="affiliate-apply-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Church or ministry name">
                <Input required value={form.org_name} className="fwm-field"
                       onChange={(e) => set('org_name')(e.target.value)}
                       data-testid="apply-org-name" placeholder="e.g. Living Word Chapel" />
              </Field>
              <Field label="This work is a">
                <Select value={form.org_type} onValueChange={set('org_type')}>
                  <SelectTrigger className="fwm-field" data-testid="apply-org-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="church">Church</SelectItem>
                    <SelectItem value="ministry">Ministry</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Title">
                <Input value={form.pastor_title} className="fwm-field"
                       onChange={(e) => set('pastor_title')(e.target.value)}
                       data-testid="apply-pastor-title" placeholder="Pastor / Apostle / Bishop" />
              </Field>
              <Field label="Pastor or leader's name">
                <Input required value={form.pastor_name} className="fwm-field"
                       onChange={(e) => set('pastor_name')(e.target.value)}
                       data-testid="apply-pastor-name" />
              </Field>
              <Field label="Country">
                <Select value={form.country} onValueChange={set('country')}>
                  <SelectTrigger className="fwm-field" data-testid="apply-country">
                    <SelectValue placeholder="Choose a country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.name}>{c.flag} {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="City or town">
                <Input value={form.city} className="fwm-field"
                       onChange={(e) => set('city')(e.target.value)} data-testid="apply-city" />
              </Field>
              <Field label="Email">
                <Input required type="email" value={form.email} className="fwm-field"
                       onChange={(e) => set('email')(e.target.value)} data-testid="apply-email" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} className="fwm-field"
                       onChange={(e) => set('phone')(e.target.value)} data-testid="apply-phone" />
              </Field>
              <Field label="WhatsApp">
                <Input value={form.whatsapp} className="fwm-field"
                       onChange={(e) => set('whatsapp')(e.target.value)} data-testid="apply-whatsapp" />
              </Field>
              <Field label="Website">
                <Input value={form.website} className="fwm-field" placeholder="https://"
                       onChange={(e) => set('website')(e.target.value)} data-testid="apply-website" />
              </Field>
              <Field label="Years in ministry">
                <Input type="number" min="0" value={form.years_in_ministry} className="fwm-field"
                       onChange={(e) => set('years_in_ministry')(e.target.value)}
                       data-testid="apply-years" />
              </Field>
              <Field label="Congregation size" hint="Roughly — optional">
                <Input type="number" min="0" value={form.congregation_size} className="fwm-field"
                       onChange={(e) => set('congregation_size')(e.target.value)}
                       data-testid="apply-size" />
              </Field>
            </div>

            <Field label="Physical address">
              <Input value={form.address} className="fwm-field"
                     onChange={(e) => set('address')(e.target.value)} data-testid="apply-address" />
            </Field>

            <Field label="About the work"
                   hint="A short description of the ministry — at least 20 characters.">
              <Textarea required rows={5} value={form.description} className="fwm-field"
                        onChange={(e) => set('description')(e.target.value)}
                        data-testid="apply-description"
                        placeholder="Who you are, what the Lord has called you to, and how long you have been serving." />
            </Field>

            <div>
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--fwm-muted)]">
                Supporting documents · optional
              </span>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {docs.map((d, i) => (
                  <span key={d.path} className="fwm-chip flex items-center gap-2"
                        data-testid={`apply-doc-${i}`}>
                    {d.name}
                    <button type="button" onClick={() => setDocs((x) => x.filter((y) => y.path !== d.path))}
                            data-testid={`apply-doc-remove-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="fwm-btn-ghost inline-flex items-center gap-2"
                        data-testid="apply-doc-upload">
                  {uploading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Paperclip className="h-3.5 w-3.5" />}
                  Attach a file
                </button>
                <input ref={fileRef} type="file" hidden onChange={pickFile}
                       accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" />
              </div>
              <p className="mt-2 text-xs text-[color:var(--fwm-muted)]">
                Certificate of incorporation, ordination papers or anything else you would like the
                office to see. Nothing here is required.
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.show_contact}
                        onCheckedChange={(v) => set('show_contact')(Boolean(v))}
                        data-testid="apply-show-contact" className="mt-0.5" />
              <span className="text-sm text-[color:var(--fwm-muted)]">
                Show our email and phone on our public profile once we are listed.
              </span>
            </label>

            <button type="submit" disabled={busy} className="fwm-btn fwm-btn-lg"
                    data-testid="apply-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Submit application <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}
      </section>
    </FwmShell>
  );
}
